import { Boundaries } from "@planzone/core";
import { Identifiable, Planzone } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { reduceResource } from "../../common/resource";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  resources: [],
};

function handleRequest(state: State): State {
  return { request: setRequestStateToPending(), resources: [] };
}

function handleSuccess(
  state: State,
  { resources }: Boundaries["retrievePlanzoneResources"]["success"],
): State {
  return {
    ...state,
    request: setRequestStateToSuccess(),
    resources,
  };
}

function handleFailure(state: State, planzone: Identifiable<Planzone>): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleMaterialResourceAddition(
  state: State,
  { resource }: Boundaries["addPlanzoneMaterialResource"]["success"],
): State {
  return {
    ...state,
    request: setRequestStateToSuccess(),
    resources: [resource, ...state.resources],
  };
}

function handleProjectMaterialResourceAddition(
  state: State,
  projectResource: Boundaries["addProjectMaterialResource"]["success"],
): State {
  return {
    ...state,
    request: setRequestStateToSuccess(),
    resources: [projectResource.resource, ...state.resources],
  };
}

function handleVirtualResourceAddition(
  state: State,
  { resource }: Boundaries["addPlanzoneVirtualResource"]["success"],
): State {
  return {
    ...state,
    request: setRequestStateToSuccess(),
    resources: [resource, ...state.resources],
  };
}

function handleProjectVirtualResourceAddition(
  state: State,
  projectResource: Boundaries["addProjectVirtualResource"]["success"],
): State {
  return {
    ...state,
    request: setRequestStateToSuccess(),
    resources: [projectResource.resource, ...state.resources],
  };
}

function handleResourceRemoval(
  state: State,
  { resource }: Boundaries["removeResourceFromPlanzone"]["success"],
): State {
  return {
    ...state,
    resources: state.resources
      .filter(({ uuid }) => resource.uuid !== uuid)
      .map((res) => ({
        ...res,
        subordinates: (res.subordinates ?? []).filter(
          (s) => s.uuid !== resource.uuid,
        ),
      })),
  };
}

function handleInvitationsSuccess(
  state: State,
  { members }: Boundaries["inviteMembers"]["success"],
): State {
  return {
    ...state,
    resources: [...members.map(({ resource }) => resource), ...state.resources],
  };
}

function handleInvitationsFailure(
  state: State,
  { succeededInvitations }: Boundaries["inviteMembers"]["failure"],
): State {
  return {
    ...state,
    resources: [
      ...succeededInvitations.map(({ resource }) => resource),
      ...state.resources,
    ],
  };
}

function handleDisableResource(
  state: State,
  resource: Boundaries["disableResource"]["success"],
): State {
  return {
    ...state,
    resources: state.resources.map((res) =>
      res.uuid === resource.uuid ? resource : res,
    ),
  };
}

function handleActivateResource(
  state: State,
  resource: Boundaries["activateResource"]["success"],
): State {
  return {
    ...state,
    resources: state.resources.map((res) =>
      res.uuid === resource.uuid ? resource : res,
    ),
  };
}

function handleAssignManagerToResourceSuccess(
  state: State,
  { resource, manager }: Boundaries["assignManagerToResource"]["success"],
): State {
  return {
    ...state,
    resources: state.resources.map((res) =>
      res.uuid === manager.uuid
        ? {
            ...res,
            subordinates: [
              ...(res.subordinates ?? []),
              {
                ...resource,
              },
            ],
          }
        : res,
    ),
  };
}

function handleUnAssignManagerFromResourceSuccess(
  state: State,
  { resource, manager }: Boundaries["unAssignManagerFromResource"]["success"],
): State {
  return {
    ...state,
    resources: state.resources.map((res) =>
      res.uuid === manager.uuid
        ? {
            ...res,
            subordinates: (res.subordinates ?? []).filter(
              (s) => s.uuid !== resource.uuid,
            ),
          }
        : res,
    ),
  };
}
function handleAssignEveryoneToManagerSuccess(
  state: State,
  { manager, subordinates }: Boundaries["assignEveryoneToManager"]["success"],
): State {
  return {
    ...state,
    resources: state.resources.map((res) =>
      res.uuid === manager.uuid
        ? {
            ...res,
            subordinates: [...(res.subordinates ?? []), ...subordinates],
          }
        : res,
    ),
  };
}

function handleSubordinatesRemoval(
  state: State,
  { manager, subordinateUuids }: Boundaries["removeSubordinates"]["success"],
): State {
  return {
    ...state,
    resources: state.resources.map((res) =>
      res.uuid === manager.uuid
        ? {
            ...res,
            subordinates: (res.subordinates ?? []).filter(
              ({ uuid }) => !subordinateUuids.includes(uuid),
            ),
          }
        : res,
    ),
  };
}

function propagateEvent(state = initialState, event: Event): State {
  return {
    ...state,
    resources: state.resources.map((resource) =>
      reduceResource(resource, event),
    ),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrievePlanzoneResourcesRequested]: handleRequest,
      [events.retrievePlanzoneResourcesSucceeded]: handleSuccess,
      [events.retrievePlanzoneResourcesFailed]: handleFailure,
      [events.addPlanzoneMaterialResourceSucceeded]:
        handleMaterialResourceAddition,
      [events.addPlanzoneVirtualResourceSucceeded]:
        handleVirtualResourceAddition,
      [events.addProjectMaterialResourceSucceeded]:
        handleProjectMaterialResourceAddition,
      [events.addProjectVirtualResourceSucceeded]:
        handleProjectVirtualResourceAddition,
      [events.removeResourceFromPlanzoneSucceeded]: handleResourceRemoval,
      [events.inviteMembersSucceeded]: handleInvitationsSuccess,
      [events.inviteMembersFailed]: handleInvitationsFailure,
      [events.disableResourceSucceeded]: handleDisableResource,
      [events.activateResourceSucceeded]: handleActivateResource,
      [events.assignManagerToResourceSucceeded]:
        handleAssignManagerToResourceSuccess,
      [events.unAssignManagerFromResourceSucceeded]:
        handleUnAssignManagerFromResourceSuccess,
      [events.assignEveryoneToManagerSucceeded]:
        handleAssignEveryoneToManagerSuccess,
      [events.removeSubordinatesSucceeded]: handleSubordinatesRemoval,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
